<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="room-type-list content-wrapper">
      <div class="page-header">
        {{ $t('supplementCharge') }}
        <popover-preview-remark-component
          :hotel-id="activeHotelId"
          :remark-type="REMARK_TYPE.SUPPLEMENT">
        </popover-preview-remark-component>
      </div>
      <div>
        <el-row>
          <el-col :span="4">
            <el-input :placeholder="$t('keyword')"
                      v-model="keyword"
                      @input="isSearching = true"></el-input>
          </el-col>
          <el-col :span="20">
            <el-button style="float: right;" type="primary" icon="el-icon-plus" @click="onGotoAddButtonClick">{{
              $t('newSupplement') }}</el-button>
          </el-col>
        </el-row>
        <div style="margin-top: 25px">
          <el-table
            id="room-type-list-table"
            :data="supplementListDataSource"
            :default-sort = "{prop: 'name', order: 'ascending'}"
            header-row-class-name="table-header-color"
            v-loading="isSearching"
            style="width: 100%">
            <el-table-column
              :label="$t('isDefault')"
              width="90px">
              <template slot-scope="scope">
                <el-checkbox v-model="isDefaultList[scope.row.id]" :label="scope.row.id" @change="onDefaultChange(scope.row.id)"> &nbsp; </el-checkbox>
              </template>
            </el-table-column>
            <el-table-column
              prop="name"
              :label="$t('name')">
            </el-table-column>
            <el-table-column
              :label="$t('type')">
              <template slot-scope="scope">{{ getSupplementTypeName(scope.row.supplementTypeId) }}</template>
            </el-table-column>
            <el-table-column
              align="right"
              :label="$t('rate')">
              <template slot-scope="scope">{{ bindPrice(scope.row) }}</template>
            </el-table-column>
            <el-table-column
              align="center"
              :label="$t('periodOfStay')">
              <template slot-scope="scope">
                <span v-if="isAllTheTime(scope.row.periodOfStayList)">{{ $t('allTheTime') }}</span>
                <div v-for="(periodOfStay, index) of scope.row.periodOfStayList" :key="`period-${index}`">
                  <span> {{bindPeriodOfStay(periodOfStay.startDate, periodOfStay.endDate)}}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('conditionDetail')">
              <template slot-scope="scope">{{ getConditionDetail(scope.row) }}</template>
            </el-table-column>
            <el-table-column align="center">
              <template slot-scope="scope">
                <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onGotoEditButtonClick(scope.row)"></el-button>
                <el-button type="danger" size="small" icon="el-icon-delete" plain circle @click="onRemoveSupplementChargeButtonClick(scope.row)"></el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import { STATUS, SUPPLEMENT_PRICE_TYPE, REMARK_TYPE } from '@/configs/constant'
import i18nMessages from '@/translations/Hotel/ManageHotel/Supplement/ManageSupplement/Item/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import HotelSupplementApi from '@/api/hotel/supplement-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import LocalStorageFactory from '@/factories/local-storage-factory'
import PopoverPreviewRemarkComponent from '../../../../components/PopoverPreviewRemarkComponent'
import { filterDataByKeyword } from '@/services/general-service'
import { get, debounce } from 'lodash'
import { mapState } from 'vuex'
import { supplementUnitTypeList, getUnitTypeTitleKey, getHotelIndexRoute } from '@/services/hotel-service'
import { numberFormat, bindData } from '@/helpers'
import { dateFormat } from '@/helpers/datetime'

export default {
  name: 'HotelSupplementItemList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer, PopoverPreviewRemarkComponent },
  i18n: { messages: i18nMessages },
  created: async function () {
    if (this.activeHotelId === undefined || this.activeHotelId === '') {
      const companyDetail = LocalStorageFactory.getCompanyDetail()
      const routeName = await getHotelIndexRoute(companyDetail, this.$store)

      return this.$router.push({
        name: routeName
      })
    }

    try {
      this.isLoading = true

      const supplement = await HotelSupplementApi.list(this.activeHotelId)
      this.supplementList = supplement.supplementList
      this.supplementTypeList = supplement.supplementTypeList
      this.isDefaultList = this._prepareIsDefaultList()
      this.getSupplementListDataSource()
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  computed: {
    ...mapState({
      activeHotelId: state => state.general.activeHotelId
    })
  },
  data () {
    return {
      STATUS,
      supplementUnitTypeList,
      isLoading: true,
      keyword: '',
      isSearching: false,
      supplementList: [],
      supplementTypeList: [],
      isDefaultList: {},
      REMARK_TYPE,
      supplementListDataSource: []
    }
  },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this.getSupplementListDataSource()
      }
    }
  },
  methods: {
    getSupplementListDataSource: function () {
      const searchKeys = ['name', 'conditionDetail']
      const filteredSupplementList = filterDataByKeyword(this.supplementList, this.keyword, searchKeys)
      this.supplementListDataSource = [...filteredSupplementList]
    },
    getSupplementIsInRoomRate: function (status) {
      switch (status) {
        case STATUS.ENABLE:
          return 'Yes'
        case STATUS.DISABLE:
          return 'No'
        default:
          return 'N/A'
      }
    },
    getSupplementTypeName: function (supplementTypeId) {
      const supplementType = this.supplementTypeList.find(item => item.id === supplementTypeId)

      return (!supplementType) ? 'N/A' : supplementType.name
    },
    bindPrice: function (row) {
      const suffix = (row.priceType === SUPPLEMENT_PRICE_TYPE.NET) ? '.-' : '%'
      const unitTypeTitleKey = getUnitTypeTitleKey(row.unitType)
      const supplementType = this.$t(`${unitTypeTitleKey}`)
      const price = numberFormat(row.price, suffix)
      return `${price} ${supplementType}`
    },
    isAllTheTime: function (periodOfStayList) {
      return (periodOfStayList === undefined || periodOfStayList.length === 0)
    },
    bindPeriodOfStay: function (startDate, endDate) {
      const fromDate = new Date(startDate)
      const toDate = new Date(endDate)

      return `${dateFormat.short(fromDate)} - ${dateFormat.short(toDate)}`
    },
    getConditionDetail: function (row) {
      return bindData(row, 'conditionDetail')
    },
    onGotoAddButtonClick: function () {
      this.$router.push({
        name: 'HotelSupplementItemAdd',
        params: {
          supplementTypeList: this.supplementTypeList
        }
      })
    },
    onGotoEditButtonClick: function (supplementCharge) {
      this.$router.push({
        name: 'HotelSupplementItemEdit',
        params: {
          supplementCharge: supplementCharge,
          supplementTypeList: this.supplementTypeList
        }
      })
    },
    onRemoveSupplementChargeButtonClick: async function (row) {
      const confirmMessage = this.$t('confirmRemoveSupplementMessage', {
        supplementChargeName: row.name
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }

      this.removeSupplementCharge(row.id)
    },
    removeSupplementCharge: async function (supplementId) {
      try {
        this.startLoading()

        await HotelSupplementApi.remove(this.activeHotelId, supplementId)
        this.supplementList = this.supplementList.filter(item => item.id !== supplementId)
        this.getSupplementListDataSource()

        const notificationTitle = this.$t('removeSupplementSuccess')
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onDefaultChange: async function (supplementId) {
      this.startLoading()
      try {
        const isDefault = (this.isDefaultList[supplementId] === true) ? 1 : 0
        await this._updateIsDefault(supplementId, isDefault)

        const title = this.$t('updateIsDefaultSuccessTitle')
        const message = this.$t('updateIsDefaultSuccessMessage')
        this.showNotificationSuccess(title, message)
      } catch (err) {
        this.alertError(err.message)
      }
      this.stopLoading()
    },
    _updateIsDefault: async function (supplementId, isDefault) {
      const data = {
        isDefault: isDefault
      }
      await HotelSupplementApi.updateIsDefault(this.activeHotelId, supplementId, data)
    },
    _prepareIsDefaultList: function () {
      const isDefaultList = {}
      this.supplementList.forEach((supplement) => {
        const supplementId = supplement.id
        isDefaultList[supplementId] = Boolean(get(supplement, 'isDefault', false))
      })

      return isDefaultList
    }
  }
}
</script>

<style scoped>
  #room-type-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  .el-form > div {
    text-align: left;
  }
</style>
